import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, EnvelopeSimple, IdentificationCard } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Something went wrong. Try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-zinc-100" data-testid="forgot-root">
      <div className="flex flex-col justify-between p-8 lg:p-14 bg-zinc-950 text-white">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white" data-testid="forgot-back">
          <ArrowLeft size={16} /> Back to sign in
        </Link>
        <div>
          <div className="inline-flex items-center gap-2 mb-8">
            <div className="w-9 h-9 bg-white text-zinc-950 flex items-center justify-center rounded-sm">
              <IdentificationCard weight="fill" size={20} />
            </div>
            <div className="font-display font-black text-xl">SaarOne</div>
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl leading-tight tracking-tight">Forgot your password?</h1>
          <p className="text-zinc-400 mt-6 text-lg max-w-md leading-relaxed">Tell us your work email and we'll send a one-time link to set a new one.</p>
        </div>
        <div className="text-xs tracking-widest uppercase text-zinc-500">© 2026 SaarOne</div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-md">
          {sent ? (
            <div data-testid="forgot-sent">
              <EnvelopeSimple size={48} className="text-zinc-900"/>
              <h2 className="font-display font-bold text-2xl mt-4">Check your inbox.</h2>
              <p className="text-sm text-zinc-500 mt-3 leading-relaxed">If <strong>{email}</strong> is on our records, a password-reset link is on its way. The link expires in 60 minutes.</p>
              <p className="text-xs text-zinc-400 mt-6">Didn't receive it? Check your spam folder, or <button onClick={() => setSent(false)} className="underline">try a different email</button>.</p>
              <Link to="/login" className="mt-8 inline-block text-sm font-medium underline">Back to sign in</Link>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Reset password</div>
              <h2 className="font-display font-bold text-3xl mt-2">Send me a link.</h2>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-medium text-zinc-600">Work email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="mt-2 h-11 bg-white border-zinc-300 rounded-md" data-testid="forgot-email-input"/>
                </div>
                {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="forgot-error">{err}</div>}
                <Button type="submit" disabled={busy} className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 rounded-md" data-testid="forgot-submit">
                  {busy ? "Sending…" : "Email me a reset link"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
